import { useMemo } from 'react';

import { filterNamedVersions } from './utils';
import { variantLookupKey } from 'src/pages/workflow-builder-edit/utils/buildVariantLookup';
import { useListAbTestConfigsQuery } from 'src/services/agent-ab-test/agentAbTestApi';
import { useGetAutoflowVersionHistoryV2Query } from 'src/services/workflowBuilderAutoflowApi.ts/workflowBuilderAutoflowApi';
import { AutoflowVersionHistorySummary } from 'src/types/workflowBuilderAPITypes';

interface UseVersionHistoryOptions {
  intentWorkflowId: string;
  /** Pre-fetched version history data - if provided, skips API call */
  prefetchedData?: AutoflowVersionHistorySummary[];
  skip?: boolean;
}

interface UseVersionHistoryResult {
  /** Current versions to display - includes live version (or latest auto-saved) plus any versions in active A/B tests */
  currentVersions: AutoflowVersionHistorySummary[];
  /** Whether there's no published version (only auto-saved state) */
  isAutoSaved: boolean;
  /** Error state */
  isError: boolean;
  /** Loading state */
  isLoading: boolean;
  /** The highest version number in the history */
  maxVersion: number;
  /** Named/saved versions to show in "Versions" section (excludes versions in active A/B tests) */
  namedVersions: AutoflowVersionHistorySummary[];
  /** Versions that have been published (version_type === 'published') */
  publishedVersions: AutoflowVersionHistorySummary[];
  /** Refetch the version history */
  refetch: () => void;
  /** All versions from the API */
  versionHistory: AutoflowVersionHistorySummary[];
}

/**
 * Helper to sort versions by date (newest first)
 */
const sortByDateDesc = (
  a: AutoflowVersionHistorySummary,
  b: AutoflowVersionHistorySummary,
) => {
  const dateA = new Date(a.last_updated_at || 0).getTime();
  const dateB = new Date(b.last_updated_at || 0).getTime();
  return dateB - dateA;
};

/**
 * Hook that provides filtered version history data for dropdowns and version lists.
 * Consolidates version filtering logic to ensure consistency across components.
 */
export const useVersionHistory = ({
  intentWorkflowId,
  prefetchedData,
  skip = false,
}: UseVersionHistoryOptions): UseVersionHistoryResult => {
  const {
    currentData: fetchedData = [],
    isError,
    isLoading,
    refetch,
  } = useGetAutoflowVersionHistoryV2Query(intentWorkflowId, {
    // Skip API call if prefetched data is provided
    skip: !intentWorkflowId || skip || prefetchedData !== undefined,
  });

  // Use prefetched data if available, otherwise use fetched data
  const versionHistory = prefetchedData ?? fetchedData;

  const { data: abTestConfigsData } = useListAbTestConfigsQuery({
    page: 1,
    pageSize: 100,
  });

  const result = useMemo(() => {
    // Build set of version keys that are in active A/B tests
    const activeAbTestVersionKeys = new Set<string>();
    for (const config of abTestConfigsData?.ab_test_configs ?? []) {
      if (
        config.intent_workflow_id !== intentWorkflowId ||
        config.status !== 'running'
      ) {
        continue;
      }
      for (const variant of config.variants ?? []) {
        const key = variantLookupKey(
          variant.intent_workflow_version,
          variant.version_type,
        );
        activeAbTestVersionKeys.add(key);
      }
    }

    // Find live version (is_published=true), sorted by date
    const liveVersion = versionHistory
      .filter(v => v.is_published === true)
      .sort(sortByDateDesc)[0];

    // If no live version, get latest auto-saved
    const latestAutoSaved = versionHistory
      .filter(v => v.version_type === 'auto_saved')
      .sort(sortByDateDesc)[0];

    // Primary current version is live if exists, otherwise latest auto-saved
    const primaryCurrentVersion = liveVersion || latestAutoSaved;

    // Find all versions in active A/B tests
    const abTestVersions = versionHistory.filter(v => {
      const key = variantLookupKey(v.version, v.version_type);
      return activeAbTestVersionKeys.has(key);
    });

    // Build currentVersions array - include primary current version plus any A/B test versions
    const currentVersions: AutoflowVersionHistorySummary[] = [];
    const addedKeys = new Set<string>();

    // Add primary current version first (if exists)
    if (primaryCurrentVersion) {
      const key = variantLookupKey(
        primaryCurrentVersion.version,
        primaryCurrentVersion.version_type,
      );
      currentVersions.push(primaryCurrentVersion);
      addedKeys.add(key);
    }

    // Add A/B test versions that aren't already added
    for (const v of abTestVersions) {
      const key = variantLookupKey(v.version, v.version_type);
      if (!addedKeys.has(key)) {
        currentVersions.push(v);
        addedKeys.add(key);
      }
    }

    const publishedVersions = versionHistory.filter(
      v => v.version_type === 'published',
    );

    // Filter named versions, excluding those in active A/B tests
    const namedVersions = filterNamedVersions(
      versionHistory,
      publishedVersions,
    ).filter(v => {
      const key = variantLookupKey(v.version, v.version_type);
      return !activeAbTestVersionKeys.has(key);
    });

    const isAutoSaved = versionHistory.length === 0 || !liveVersion;

    const maxVersion =
      versionHistory.length > 0
        ? Math.max(...versionHistory.map(v => v.version))
        : 0;

    return {
      currentVersions,
      isAutoSaved,
      maxVersion,
      namedVersions,
      publishedVersions,
      versionHistory,
    };
  }, [abTestConfigsData, intentWorkflowId, versionHistory]);

  return {
    ...result,
    isError,
    isLoading,
    refetch,
  };
};
